import { useEffect, useState } from "react";

/**
 * Internal microcopy of the design-system components (labels that never reach the apps' translation files).
 * The active language is read from `<html lang>`, which the i18n package keeps in sync, so the UI package
 * stays free of an i18n dependency while still rendering Arabic and English.
 */
const MICROCOPY = {
  en: {
    close: "Close",
    you: "You",
    showDraft: "Show draft & citations",
    hideDraft: "Hide draft",
    noResults: "No results",
    previousPage: "Previous page",
    nextPage: "Next page",
    switchLanguage: "Switch language",
    mainMenu: "Main menu",
    riskHigh: "High risk",
    riskElevated: "Elevated risk",
    riskLow: "Low risk",
    breadcrumb: "You are here",
    toggleNavigation: "Toggle navigation",
    collapseSidebar: "Collapse sidebar",
    expandSidebar: "Expand sidebar",
    moderatelyConcentrated: "Moderately concentrated",
    highlyConcentrated: "Highly concentrated",
    quality: "AI check",
    confidence: "AI assistant's confidence",
    qualityDefinition: "Score from a second AI that checks the draft against its sources, 0 to 100. It is not a verdict.",
    confidenceDefinition: "How sure the AI assistant says it is, 0 to 100%. The assistant's own estimate, not a measured accuracy.",
    hhiPre: "HHI pre",
    hhiPost: "HHI post",
    editorBold: "Bold",
    editorItalic: "Italic",
    editorStrike: "Strikethrough",
    editorHeading: "Heading",
    editorSubheading: "Subheading",
    editorBulletList: "Bulleted list",
    editorOrderedList: "Numbered list",
    editorTaskList: "Checklist",
    editorQuote: "Quote",
    editorLink: "Link",
    editorUnlink: "Remove link",
    editorLinkPrompt: "Link address",
    editorTable: "Insert table",
    editorUndo: "Undo",
    daysLeft: "days left",
    daysOver: "days over",
    paused: "Paused",
    editorRedo: "Redo",
    editorPlaceholder: "Write here…",
    editorFormatting: "Formatting",
    confirm: "Confirm",
    cancel: "Cancel",
    irreversible: "This cannot be undone.",
    search: "Search",
    searchPlaceholder: "Search files, companies and reference numbers",
    searchEmpty: "Nothing matches. Try a file number, a company name or part of a title.",
    searchHint: "Type at least two letters",
    reportIssue: "Report an issue",
    reportIssueTitle: "Report an issue on this screen",
    reportIssueHint: "Describe what you expected and what happened. A picture of the screen and the page address are attached automatically.",
    reportIssueCategory: "Type",
    reportIssueWording: "Wording",
    reportIssueLayout: "Layout",
    reportIssueError: "Error",
    reportIssueSuggestion: "Suggestion",
    reportIssueOther: "Other",
    reportIssueDescription: "What happened",
    reportIssueScreenshot: "Attach a picture of the screen",
    reportIssueSend: "Send report",
    reportIssueSent: "Thank you. The report has reached the platform team.",
    reportIssueFailed: "The report could not be sent. Try again.",
    sessionTitle: "Your session is about to end",
    sessionBody: "For your security you will be signed out in {time}. Unsaved text is kept on this device and restored when you sign in again.",
    sessionExtend: "Stay signed in",
    sessionSignOut: "Sign out now",
    sessionExtended: "Your session has been extended.",
    sessionEnded: "Your session has ended. Sign in again to continue; unsaved text will be restored.",
    whatsNew: "What's new",
    whatsNewTitle: "What's new in version {version}",
    changed: "Changed",
    added: "Added",
    removed: "Removed",
    version: "Version",
    draftRestored: "Your unsaved text was restored.",
    draftDiscard: "Discard",
    working: "Working…",
    citationOpen: "Click to open the source",
    citationUnverified: "The citation check could not confirm this excerpt supports the claim.",
    citationNotInCorpus: "The legal corpus does not hold this article: correct the reference or record why it stands.",
    citationNoExcerpt: "No excerpt was captured for this source.",
    citationPage: "page {page}",
    citationUpdated: "Source updated {date}",
    citationChecked: "Checked {date}",
    citationSite: "Open the publisher's page",
    sourceRegulation: "Legislation",
    sourceDocument: "Document",
    sourceDataBank: "Statistics",
    sourceWeb: "Web page",
    sourcePrecedent: "Precedent",
    sourceComparativeReference: "Comparative reference",
    sourceTool: "Tool result",
    send: "Send",
    stop: "Stop",
    attach: "Attach files",
    dropFiles: "Drop files to attach them",
    jumpToLatest: "Jump to latest",
    thinking: "Thinking…",
    workedSteps: "Worked through {count} steps",
    workedOneStep: "Worked through 1 step",
    usedSources: "Used {count} sources",
    usedOneSource: "Used 1 source",
    stepFailed: "Did not finish",
    remove: "Remove",
    uploading: "Uploading…",
    apply: "Apply",
    applied: "Applied",
    undo: "Undo",
    // The shared save helper's messages (S 2.8): "saved" only once the API stored the change; otherwise the cause.
    saved: "Saved",
    saveFailed: "Not saved",
    saveOffline: "This device is offline, so nothing was sent. Reconnect and try again.",
    saveNetwork: "The server could not be reached, so nothing was saved. Check the connection and try again.",
    saveSession: "Your session has ended, so this was not saved. Sign in again and repeat it.",
    saveForbidden: "You do not have permission for this action.",
    saveNotFound: "The record is no longer there; it may have been moved or closed.",
    saveConflict: "The record changed after it was opened. Reload it and make the change again.",
    saveInvalid: "The server refused the data sent.",
    saveTooLarge: "The file or text is too large to be stored.",
    saveBusy: "Too many requests at once. Wait a moment and try again.",
    saveServer: "The server could not complete the request. Try again, and report it if it happens again.",
    saveUnconfirmed: "The answer did not confirm the save. Reload to see what was stored before trying again.",
    saveRefused: "The platform did not make the change.",
    stepRefused: "The file did not move to the next step",
    copyFailed: "The text could not be copied. Select it and copy it by hand.",
    approvedTranslation: "Machine translation of the approved text. The approved document is the English text.",
    // The letters a step sends, shown in full before it is confirmed (release 1.6).
    stepLetterOne: "The letter this step sends",
    stepLettersTitle: "The letters this step sends ({count})",
    stepLettersHint: "Filled from the file, as the step will send them. They go out in your name once you confirm.",
    stepLettersApprovalHint: "Approving takes the step the draft proposes. These letters go out with it, in your name, as shown.",
    stepLetterUnverified: "Cites provisions not yet verified by the Department",
    stepLetterUnverifiedHint: "Not yet checked against the Arabic text in the Official Gazette. Sent by you, the letter goes out, and these provisions are recorded with the sending in the file's history.",
    stepLettersLoading: "Checking which letters this step sends…",
    stepLettersFailed: "The letters this step sends could not be shown.",
    stepLettersConfirmTitle: "Read the letters this sends",
    stepLettersConfirm: "Confirm and send",
    stepLetterTo: "To {name}",
    stepLetterLegalEffect: "Legal effect",
    stepLetterCourtesy: "Courtesy letter",
    stepLetterEnglish: "English",
    stepLetterArabic: "Arabic",
    letterCompletionRequest: "Completion request",
    letterDocumentsToFollow: "Request for the documents allowed to follow the filing",
    letterImplicitRejection: "Notice of the implicit rejection",
    letterReviewExtended: "Notice of the extension of the review period",
    letterWorkflowAction: "Letter drawn on the step in the Workflow Builder",
  },
  ar: {
    close: "إغلاق",
    you: "أنت",
    showDraft: "عرض المسودة والاستشهادات",
    hideDraft: "إخفاء المسودة",
    noResults: "لا توجد نتائج",
    previousPage: "الصفحة السابقة",
    nextPage: "الصفحة التالية",
    switchLanguage: "تبديل اللغة",
    mainMenu: "القائمة الرئيسية",
    riskHigh: "مخاطر عالية",
    riskElevated: "مخاطر متوسطة",
    riskLow: "مخاطر منخفضة",
    breadcrumb: "موقعك في المنصة",
    toggleNavigation: "تبديل التنقل",
    collapseSidebar: "طي الشريط الجانبي",
    expandSidebar: "توسيع الشريط الجانبي",
    moderatelyConcentrated: "مركز بدرجة متوسطة",
    highlyConcentrated: "مركز بدرجة عالية",
    quality: "الفحص الآلي",
    confidence: "ثقة المساعد",
    qualityDefinition: "درجة يمنحها ذكاء اصطناعي ثان يفحص المسودة مقابل مصادرها، من 0 إلى 100، وليست حكما.",
    confidenceDefinition: "مدى تيقن مساعد الذكاء الاصطناعي كما يقدره بنفسه، من 0 إلى 100%، وليس دقة مقيسة.",
    hhiPre: "قبل الصفقة",
    hhiPost: "بعد الصفقة",
    editorBold: "غامق",
    editorItalic: "مائل",
    editorStrike: "يتوسطه خط",
    editorHeading: "عنوان",
    editorSubheading: "عنوان فرعي",
    editorBulletList: "قائمة نقطية",
    editorOrderedList: "قائمة مرقمة",
    editorTaskList: "قائمة تحقق",
    editorQuote: "اقتباس",
    editorLink: "رابط",
    editorUnlink: "إزالة الرابط",
    editorLinkPrompt: "عنوان الرابط",
    editorTable: "إدراج جدول",
    editorUndo: "تراجع",
    daysLeft: "يوم متبق",
    daysOver: "يوم تأخير",
    paused: "موقوفة",
    editorRedo: "إعادة",
    editorPlaceholder: "النص هنا…",
    editorFormatting: "التنسيق",
    confirm: "تأكيد",
    cancel: "إلغاء",
    irreversible: "لا يمكن التراجع عن هذا الإجراء.",
    search: "بحث",
    searchPlaceholder: "البحث عن ملف أو منشأة أو مرجع",
    searchEmpty: "لا توجد نتائج مطابقة. ويمكن البحث برقم الملف أو اسم المنشأة أو جزء من العنوان.",
    searchHint: "يلزم حرفان على الأقل",
    reportIssue: "الإبلاغ عن مشكلة",
    reportIssueTitle: "الإبلاغ عن مشكلة في هذه الشاشة",
    reportIssueHint: "صف ما كنت تتوقعه وما حدث. ترفق صورة الشاشة وعنوان الصفحة تلقائيا.",
    reportIssueCategory: "النوع",
    reportIssueWording: "الصياغة",
    reportIssueLayout: "التصميم",
    reportIssueError: "خطأ",
    reportIssueSuggestion: "اقتراح",
    reportIssueOther: "أخرى",
    reportIssueDescription: "ما الذي حدث",
    reportIssueScreenshot: "إرفاق صورة الشاشة",
    reportIssueSend: "إرسال البلاغ",
    reportIssueSent: "شكرا لك. وصل البلاغ إلى فريق المنصة.",
    reportIssueFailed: "تعذر إرسال البلاغ. يرجى المحاولة مرة أخرى.",
    sessionTitle: "توشك الجلسة على الانتهاء",
    sessionBody: "حفاظا على أمان حسابك سيتم تسجيل الخروج خلال {time}. يحفظ النص غير المحفوظ على هذا الجهاز ويستعاد عند تسجيل الدخول مجددا.",
    sessionExtend: "متابعة الجلسة",
    sessionSignOut: "تسجيل الخروج الآن",
    sessionExtended: "تم تمديد الجلسة.",
    sessionEnded: "انتهت الجلسة. يرجى تسجيل الدخول للمتابعة، وسيستعاد النص غير المحفوظ.",
    whatsNew: "ما الجديد",
    whatsNewTitle: "ما الجديد في الإصدار {version}",
    changed: "ما تغير",
    added: "ما أضيف",
    removed: "ما أزيل",
    version: "الإصدار",
    draftRestored: "تمت استعادة النص غير المحفوظ.",
    draftDiscard: "تجاهل",
    working: "جار العمل…",
    citationOpen: "النقر لفتح المصدر",
    citationUnverified: "لم يؤكد فحص الإحالات أن هذا المقتطف يدعم الادعاء.",
    citationNotInCorpus: "لا تتضمن المدونة القانونية هذه المادة: يصحح المرجع أو يسجل سبب الإبقاء عليه.",
    citationNoExcerpt: "لم يحفظ مقتطف من هذا المصدر.",
    citationPage: "الصفحة {page}",
    citationUpdated: "تحديث المصدر: {date}",
    citationChecked: "آخر فحص: {date}",
    citationSite: "فتح صفحة الناشر",
    sourceRegulation: "تشريع",
    sourceDocument: "مستند",
    sourceDataBank: "إحصاءات",
    sourceWeb: "صفحة ويب",
    sourcePrecedent: "سابقة",
    sourceComparativeReference: "مرجع مقارن",
    sourceTool: "نتيجة أداة",
    send: "إرسال",
    stop: "إيقاف",
    attach: "إرفاق ملفات",
    dropFiles: "أفلت الملفات هنا لإرفاقها",
    jumpToLatest: "الانتقال إلى الأحدث",
    thinking: "جار التفكير…",
    workedSteps: "الخطوات المنجزة: {count}",
    workedOneStep: "الخطوات المنجزة: 1",
    usedSources: "المصادر المستخدمة: {count}",
    usedOneSource: "المصادر المستخدمة: 1",
    stepFailed: "لم تكتمل",
    remove: "إزالة",
    uploading: "جار الرفع…",
    apply: "تطبيق",
    applied: "تم التطبيق",
    undo: "تراجع",
    saved: "تم الحفظ",
    saveFailed: "لم يحفظ",
    saveOffline: "هذا الجهاز غير متصل بالشبكة، فلم يرسل شيء. أعد الاتصال ثم حاول مجددا.",
    saveNetwork: "تعذر الوصول إلى الخادم، فلم يحفظ شيء. تحقق من الاتصال ثم أعد المحاولة.",
    saveSession: "انتهت جلستك، فلم يحفظ هذا. سجل الدخول مجددا ثم أعد الإجراء.",
    saveForbidden: "ليست لديك صلاحية لهذا الإجراء.",
    saveNotFound: "لم يعد السجل موجودا؛ ربما نقل أو أغلق.",
    saveConflict: "تغير السجل بعد فتحه. أعد تحميله ثم أجر التغيير مجددا.",
    saveInvalid: "رفض الخادم البيانات المرسلة.",
    saveTooLarge: "الملف أو النص أكبر من أن يحفظ.",
    saveBusy: "طلبات كثيرة في وقت واحد. انتظر قليلا ثم أعد المحاولة.",
    saveServer: "تعذر على الخادم إتمام الطلب. أعد المحاولة، وأبلغ عن المشكلة إن تكررت.",
    saveUnconfirmed: "لم يؤكد الرد الحفظ. أعد تحميل الصفحة للتحقق مما حفظ قبل إعادة المحاولة.",
    saveRefused: "لم تنفذ المنصة التغيير.",
    stepRefused: "لم ينتقل الملف إلى الخطوة التالية",
    copyFailed: "تعذر النسخ. حدد النص وانسخه يدويا.",
    approvedTranslation: "ترجمة آلية للنص المعتمد. المستند المعتمد هو النص الإنجليزي.",
    stepLetterOne: "الخطاب الذي ترسله هذه الخطوة",
    stepLettersTitle: "الخطابات التي ترسلها هذه الخطوة ({count})",
    stepLettersHint: "معبأة من بيانات الملف كما سترسلها الخطوة، وترسل باسمكم عند التأكيد.",
    stepLettersApprovalHint: "يتخذ الاعتماد الخطوة التي يقترحها المشروع، وترسل معها هذه الخطابات باسمكم كما هي.",
    stepLetterUnverified: "يستشهد بأحكام لم تتحقق منها الإدارة بعد",
    stepLetterUnverifiedHint: "لم تراجع بعد مقابل النص العربي في الجريدة الرسمية. وإذا أرسلته يرسل الخطاب، وتسجل هذه الأحكام مع الإرسال في سجل الملف.",
    stepLettersLoading: "جار التحقق من الخطابات التي ترسلها هذه الخطوة…",
    stepLettersFailed: "تعذر عرض الخطابات التي ترسلها هذه الخطوة.",
    stepLettersConfirmTitle: "الخطابات التي يرسلها هذا الإجراء",
    stepLettersConfirm: "التأكيد والإرسال",
    stepLetterTo: "إلى {name}",
    stepLetterLegalEffect: "ذو أثر قانوني",
    stepLetterCourtesy: "خطاب إعلامي",
    stepLetterEnglish: "الإنجليزية",
    stepLetterArabic: "العربية",
    letterCompletionRequest: "طلب الاستيفاء",
    letterDocumentsToFollow: "طلب المستندات المؤجلة عند التقديم",
    letterImplicitRejection: "إخطار بالرفض الضمني",
    letterReviewExtended: "إخطار بتمديد المدة القانونية لدراسة الطلب",
    letterWorkflowAction: "خطاب محدد على الخطوة في مصمم سير العمل",
  },
} as const;

export type MicrocopyKey = keyof (typeof MICROCOPY)["en"];
export type UiLocale = keyof typeof MICROCOPY;

export function documentLocale(): UiLocale {
  if (typeof document === "undefined") return "en";
  return document.documentElement.lang.toLowerCase().startsWith("ar") ? "ar" : "en";
}

/** Reacts to language switches by observing the `lang` attribute the i18n layer writes on `<html>`. */
export function useDocumentLocale(): UiLocale {
  const [locale, setLocale] = useState<UiLocale>(documentLocale);
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(() => setLocale(documentLocale()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });
    return () => observer.disconnect();
  }, []);
  return locale;
}

export function microcopy(key: MicrocopyKey, locale: UiLocale = documentLocale(), values?: Record<string, string | number>): string {
  const text: string = MICROCOPY[locale][key] ?? MICROCOPY.en[key];
  return values ? text.replace(/\{(\w+)\}/g, (m, k: string) => (k in values ? String(values[k]) : m)) : text;
}

export function useMicrocopy(): (key: MicrocopyKey, values?: Record<string, string | number>) => string {
  const locale = useDocumentLocale();
  return (key, values) => microcopy(key, locale, values);
}
